<template>
    <v-dialog
        :value="value"
        :persistent="true"
        max-width="600px"
        content-class="change-owner-dialog"
    >
        <v-card class="modal--tips4y">
            <v-card-title class="headline d-flex justify-center">
                <h2 class="mb-0">
                    {{ $t("request-manager.edit-ticket.change-owner.title") }}
                </h2>
                <div class="icon-close" @click="close">
                    <div>
                        <img
                            height="20"
                            onload="SVGInject(this)"
                            src="/images/_SVGs/environmental/fechar.svg"
                            width="20"
                        />
                    </div>
                </div>
            </v-card-title>

            <v-card-text>
                <p v-if="currentCustomer" class="mb-2">
                    {{ $t("request-manager.edit-ticket.change-owner.current") }}
                    <strong>{{ currentCustomer }}</strong>
                </p>
                <p class="mb-4">
                    {{
                        $t("request-manager.edit-ticket.change-owner.description")
                    }}
                </p>

                <multiselect
                    v-model="selectedUser"
                    :close-on-select="true"
                    :internal-search="false"
                    :loading="isLoading"
                    :max-height="300"
                    :options="users.data"
                    :placeholder="
                        $t('request-manager.edit-ticket.change-owner.placeholder')
                    "
                    :searchable="true"
                    :show-labels="false"
                    data-cy="change-owner-select"
                    label="text"
                    open-direction="bottom"
                    track-by="value"
                    @search-change="asyncFind"
                >
                    <template slot="noResult">
                        {{ $t("backoffice.switch-account.no-results") }}
                    </template>
                    <template slot="noOptions">
                        {{ $t("backoffice.switch-account.no-results") }}
                    </template>
                    <template slot="afterList">
                        <div
                            v-if="users.hasMorePages"
                            class="w-100 d-flex justify-content-center"
                        >
                            <v-btn
                                class="danger-button w-80 text-muted"
                                text
                                @click="loadMoreUsers"
                            >
                                <span style="text-transform: capitalize">
                                    {{
                                        $t("backoffice.modules.estimate.load-more")
                                    }}
                                </span>
                            </v-btn>
                        </div>
                    </template>
                </multiselect>

                <v-alert
                    v-if="errorMessage"
                    class="mt-4 mb-0"
                    dense
                    text
                    type="error"
                >
                    {{ errorMessage }}
                </v-alert>
            </v-card-text>

            <v-card-actions class="row-button justify-center">
                <div class="mr-2">
                    <v-btn
                        :disabled="saving"
                        class="danger-button"
                        text
                        @click="close"
                    >
                        {{ $t("cancel") }}
                    </v-btn>
                </div>
                <div class="ml-2">
                    <v-btn
                        :disabled="!selectedUser"
                        :loading="saving"
                        class="primary-button"
                        data-cy="change-owner-confirm"
                        text
                        @click="confirm"
                    >
                        {{ $t("confirm") }}
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import {
    RequestManagerServicesChangeOwner,
    RequestManagerServicesGetChangeOwnerDropdown,
} from "@services/b2b/request-manager/RequestManagerServices";
import Multiselect from "vue-multiselect";
import _ from "lodash";

export default {
    components: { Multiselect },
    props: {
        value: { type: Boolean, required: true },
        ticketId: { type: [Number, String], default: null },
        currentOwnerId: { type: [Number, String], default: null },
        currentCustomer: { type: String, default: "" },
    },
    data() {
        return {
            selectedUser: null,
            isLoading: false,
            saving: false,
            errorMessage: "",
            requestId: 0,
            users: {
                data: [],
                currentPage: 1,
                pageSize: 10,
                searchText: "",
                hasMorePages: false,
            },
        };
    },
    watch: {
        value(isOpen) {
            if (isOpen) {
                this.resetState();
                this.getUsersDropdown(true);
            }
        },
    },
    methods: {
        resetState() {
            this.selectedUser = null;
            this.errorMessage = "";
            this.users.data = [];
            this.users.currentPage = 1;
            this.users.searchText = "";
            this.users.hasMorePages = false;
        },
        close() {
            if (this.saving) return;
            this.$emit("input", false);
        },
        asyncFind: _.debounce(function (query) {
            const searchText = query || "";
            if (searchText === this.users.searchText) return;

            this.users.currentPage = 1;
            this.users.searchText = searchText;
            this.getUsersDropdown(true);
        }, 300),
        mergeUsers(currentUsers, newUsers) {
            const uniqueMap = new Map();

            [...currentUsers, ...newUsers].forEach((user) => {
                if (!uniqueMap.has(user.value)) {
                    uniqueMap.set(user.value, user);
                }
            });

            return Array.from(uniqueMap.values());
        },
        getUsersDropdown(isSearch = false) {
            const requestId = ++this.requestId;
            this.isLoading = true;

            RequestManagerServicesGetChangeOwnerDropdown({
                searchText: this.users.searchText,
                pageSize: this.users.pageSize,
                currentPage: this.users.currentPage,
            })
                .then((response) => {
                    if (requestId !== this.requestId || !response.data) return;

                    const newUsers = (response.data.result || [])
                        .map((el) => ({
                            text: el.name,
                            value: +el.value,
                        }))
                        .filter((el) => el.value !== +this.currentOwnerId);

                    this.users.data = isSearch
                        ? newUsers
                        : this.mergeUsers(this.users.data, newUsers);
                    this.users.hasMorePages =
                        response.data.currentPage < response.data.totalPages;
                })
                .catch((error) => {
                    if (error?.response?.status) {
                        this.$requestStatus.checkRequestStatus(
                            error.response.status,
                            this.$route.name,
                        );
                    }
                })
                .finally(() => {
                    if (requestId === this.requestId) {
                        this.isLoading = false;
                    }
                });
        },
        loadMoreUsers() {
            if (!this.users.hasMorePages || this.isLoading) return;

            this.users.currentPage++;
            this.getUsersDropdown();
        },
        confirm() {
            if (!this.selectedUser || this.saving) return;

            this.saving = true;
            this.errorMessage = "";
            const targetUser = { ...this.selectedUser };

            RequestManagerServicesChangeOwner(this.ticketId, {
                targetUserId: targetUser.value,
            })
                .then((response) => {
                    if (response.data.result) {
                        this.saving = false;
                        this.$emit("changed", targetUser);
                        this.close();
                    } else {
                        this.errorMessage = this.$t(
                            "request-manager.edit-ticket.change-owner.error",
                        );
                    }
                })
                .catch((error) => {
                    if (error?.response?.status) {
                        this.$requestStatus.checkRequestStatus(
                            error.response.status,
                            this.$route.name,
                        );
                    }
                    this.errorMessage =
                        error?.response?.data?.errorMessage ||
                        this.$t("request-manager.edit-ticket.change-owner.error");
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>

<style lang="scss">
.change-owner-dialog {
    overflow: visible !important;

    .v-card {
        overflow: visible !important;
    }
}
</style>
